import React, { useState } from 'react';
import styles from './_indirect-stipulation-document-dialog.module.scss';
import { Document } from '../../../Data/ApplicationDocumentData';
import { IStipulationCategory } from '../../../Interfaces/StipulationTypes';
import { ApplicationStipulation } from '../../IndirectStipsEditor/IndirectStipulation';
import { Checkbox, TextBox } from '@progress/kendo-react-inputs';
import { checkIcon } from '@progress/kendo-svg-icons';
import { Dropzone } from '../../Dropzone';
import { SvgIcon } from '@progress/kendo-react-common';
import { IAppCustomer } from '../../../Interfaces/Application/IAppCustomer';
import { IApplication } from '../../../Interfaces/Application/IApplication';
import { Constants } from '../../../utils/constants';
import { useSelector } from 'react-redux';
import { AppState } from '../../../store/rootReducer';
import { hasValue, LoadingPanel } from '@amsanalytics/react-shared-library';

export interface IndirectStipulationDocumentDialogProps {
  stepIndex: number; // Index of the selected step
  selectedDocumentId: string; // Id of the selected document
  selectedStipulationIds: number[]; // Ids of the selected stipulations to associate the document with
  documents: Document[]; // Documents user can associate with the stipulation
  documentUrls: { documentId: string, objectUrl: string | undefined, isLoading: boolean }[]; // Urls of the documents for preview
  applicationStipulation: ApplicationStipulation; // The indirect stipulation we are associating documents with.
  stipulationCategories: IStipulationCategory[]; // Stipulations user can associate with the document
  isUploadingDocument: boolean; // Whether the user is uploading a document
  onSelectDocument: (documentId: string) => void; // Callback to select a document
  onSelectStipulation: (stipulationId: number) => void; // Callback to select a stipulation
  onUploadFiles: (files: File[]) => void; // Callback to upload files
  onApplicantChecked: (checked: boolean) => void;
  onCoApplicantChecked: (checked: boolean) => void;
}

export interface IndirectStipulationDocumentDialogResult {
  documentId: string; // Id of the document to associate with the stipulations
  stipulationIds: string[]; // Ids of the stipulations to associate with the document
}

export default function IndirectStipulationDocumentDialog(props: IndirectStipulationDocumentDialogProps) {
  // Search text for the "Search Documents" searchbar
  const [searchDocumentsText, setSearchDocumentsText] = useState('');
  // Search text for the "Search Stipulations" searchbar
  const [searchStipulationsText, setSearchStipulationsText] = useState('');
  // Selected customers state (default primary customer to checked)
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);

  const application: IApplication | undefined = useSelector((state: AppState) => state.applications.editingApp);
  const primaryCustomer: IAppCustomer | undefined = application?.appCustomers.find(ac => ac.customerRoleId.toUpperCase() === Constants.roleApplicantId.toUpperCase());
  const coApplicant: IAppCustomer | undefined = application?.appCustomers.find(ac => ac.customerRoleId.toUpperCase() === Constants.roleCoApplicant1Id.toUpperCase());

  const numberOfSelectedStipulations: number = props.stipulationCategories.filter((category) => category.stipulations.some((stipulation) => props.selectedStipulationIds.includes(stipulation.id))).length;

  // Check if any customer is selected
  const noCustomersSelected = selectedCustomers.length === 0;

  // Initialize the selected customers when the component mounts
  React.useEffect(() => {
    // Default to selecting only the primary customer if they exist and there is no co-applicant
    if (primaryCustomer && !coApplicant) {
      setSelectedCustomers([primaryCustomer.id]);
      props.onApplicantChecked(true);
    }
  }, [primaryCustomer]);

  // Handle checkbox changes
  const handleCustomerCheckboxChange = (customerId: string) => {
    // Check if this is the primary customer or co-applicant
    const isPrimaryCustomer = primaryCustomer && customerId === primaryCustomer.id;
    const isCoApplicant = coApplicant && customerId === coApplicant.id;

    if (selectedCustomers.includes(customerId)) {
      // Customer is being unchecked
      setSelectedCustomers(selectedCustomers.filter(id => id !== customerId));

      // Call appropriate callback with false
      if (isPrimaryCustomer) {
        props.onApplicantChecked(false);
      }
      if (isCoApplicant) {
        props.onCoApplicantChecked(false);
      }
    } else {
      // Customer is being checked
      setSelectedCustomers([...selectedCustomers, customerId]);

      // Call appropriate callback with true
      if (isPrimaryCustomer) {
        props.onApplicantChecked(true);
      }
      if (isCoApplicant) {
        props.onCoApplicantChecked(true);
      }
    }
  };

  return (
    <div className={styles.indirectStipulationDocumentDialogContainer}>
      {props.stepIndex === 0 && (
        <div className={styles.stepContainer}>
          {/* Customer selection checkboxes */}
          <div className={styles.customerSelectionContainer}>
            <h3 style={{ margin: '0 0 10px 0' }}>Select Customers</h3>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
              {primaryCustomer && (
                <Checkbox
                  label={`${primaryCustomer.customer.firstName} ${primaryCustomer.customer.lastName} (Applicant)`}
                  checked={selectedCustomers.includes(primaryCustomer.id)}
                  onChange={() => handleCustomerCheckboxChange(primaryCustomer.id)}
                />
              )}
              {coApplicant && (
                <Checkbox
                  label={`${coApplicant.customer.firstName} ${coApplicant.customer.lastName} (Co-Applicant)`}
                  checked={selectedCustomers.includes(coApplicant.id)}
                  onChange={() => handleCustomerCheckboxChange(coApplicant.id)}
                />
              )}
            </div>
          </div>
          <br/>
          <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <p style={{ margin: '5px 0' }}>
              Choose a document to associate with the following stipulations, or upload a new one.
            </p>
            <TextBox
              value={searchDocumentsText}
              onChange={(e) => setSearchDocumentsText(e.target.value as string)}
              type='search'
              placeholder="Search documents..."
              disabled={noCustomersSelected || props.isUploadingDocument || props.documents.length === 0}
            />
            <h3 style={{ margin: 0, marginTop: '15px' }}>Existing Documents</h3>
            <div className={styles.documentsContainer}>
              {props.documents.filter((document) => document.name.toLowerCase().includes(searchDocumentsText.toLowerCase())).sort((a, b) => a.name.localeCompare(b.name)).map((document) => (
                <>
                  <div
                    key={document.documentId}
                    className={props.selectedDocumentId === document.documentId ? styles.documentItemContainerSelected : styles.documentItemContainer}
                    onClick={() => !noCustomersSelected && props.onSelectDocument(document.documentId)}
                    style={{ opacity: noCustomersSelected ? 0.5 : 1, cursor: noCustomersSelected ? 'not-allowed' : 'pointer' }}
                  >
                    <div className={styles.documentItemText}>
                      {document.name}{document.fileExtension && `${document.fileExtension}`}
                    </div>
                    {props.documentUrls.find((doc) => doc.documentId === props.selectedDocumentId)?.isLoading && (
                      <>
                        <LoadingPanel label='Loading image...' style={{ height: 30, width: 30 }} />
                        <small>Loading image...</small>
                      </>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {props.selectedDocumentId === document.documentId && (
                        <SvgIcon icon={checkIcon} size='large' color='rgb(59, 130, 246)' style={{ marginRight: '5px' }} />
                      )}
                      {props.documentUrls.find((doc) => doc.documentId === document.documentId)?.isLoading !== true && props.documents.find((doc2) => doc2.documentId === document.documentId)?.mimeType !== 'application/pdf' && (
                        <img src={props.documentUrls.find((doc) => doc.documentId === document.documentId)?.objectUrl} alt='Document' height={50} width={50} style={{ margin: '0.5em 0.5em 0 0.5em' }} />
                      )}
                    </div>
                  </div>
                </>
              ))}
              {!hasValue(searchDocumentsText) && props.documents.length === 0 && (
                <p style={{ margin: 0 }}>No documents exist. Upload a new one below.</p>
              )}
              {hasValue(searchDocumentsText) && props.documents.filter((document) => document.name.toLowerCase().includes(searchDocumentsText.toLowerCase())).length === 0 && (
                <p style={{ margin: 0 }}>No documents found for your search.</p>
              )}
            </div>
          </div>
          <div style={{ justifySelf: 'flex-end' }}>
            <h3 style={{ marginBottom: 0 }}>Upload New Document</h3>
            <Dropzone 
              disabled={noCustomersSelected || props.isUploadingDocument} 
              onDropAccepted={(acceptedFiles) => !noCustomersSelected && props.onUploadFiles(acceptedFiles)} 
            />
            {props.isUploadingDocument && (
              <div style={{ display: 'flex', alignItems: 'center', marginTop: 5 }}>
                <LoadingPanel label='Uploading...' style={{ height: 30, width: 30 }} />
              </div>
            )}
          </div>
        </div>
      )}
      {props.stepIndex === 1 && (
        <div className={styles.stepContainer}>
          <h3 style={{ margin: 0 }}>Selected Document</h3>
          <p className={styles.selectedDocumentText}>
            {props.documents.find((document) => document.documentId === props.selectedDocumentId)?.name} {props.documents.find((document) => document.documentId === props.selectedDocumentId)?.fileExtension && `${props.documents.find((document) => document.documentId === props.selectedDocumentId)?.fileExtension}`}
          </p>
          <TextBox
            value={searchStipulationsText}
            onChange={(e) => setSearchStipulationsText(e.target.value as string)}
            type='search'
            placeholder="Search stipulations..."
            disabled={noCustomersSelected}
          />
          <h3 style={{ marginBottom: 0 }}>Select Stipulations</h3>
          <div className={styles.stipulationCategoriesContainer} style={{ opacity: noCustomersSelected ? 0.5 : 1 }}>
            {props.stipulationCategories.filter((category) => category.stipulations.some((stipulation) => stipulation.name.toLowerCase().includes(searchStipulationsText.toLowerCase()))).map((category) => (
              <div key={category.id} className={styles.stipulationCategoryContainer}>
                <div className={styles.stipulationCategoryText}>{category.name}</div>
                <div className={styles.stipulationItemsContainer}>
                  {category.stipulations.map((stipulation) => (
                    <div
                      key={stipulation.id}
                      className={props.selectedStipulationIds.includes(stipulation.id) ? styles.stipulationItemContainerSelected : styles.stipulationItemContainer}
                      onClick={() => !noCustomersSelected && props.onSelectStipulation(stipulation.id)}
                      style={{ cursor: noCustomersSelected ? 'not-allowed' : 'pointer' }}>
                      <div className={styles.stipulationItemText}>{stipulation.name}</div>
                      <Checkbox
                        checked={props.selectedStipulationIds.includes(stipulation.id)}
                        color='primary'
                        disabled={noCustomersSelected}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {hasValue(searchStipulationsText) && props.stipulationCategories.filter((category) => category.stipulations.some((stipulation) => stipulation.name.toLowerCase().includes(searchStipulationsText.toLowerCase()))).length === 0 && (
              <p style={{ margin: 0 }}>No stipulations found for your search.</p>
            )}
          </div>
          {numberOfSelectedStipulations > 0 && (
            <small style={{ color: 'rgb(75, 85, 99)' }}>Selected {numberOfSelectedStipulations} stipulation{numberOfSelectedStipulations > 1 ? 's' : ''}.</small>
          )}
        </div>
      )}
    </div>
  )
}
